import "./Playstatistics.css";
import kelvin from "./playImage/kelvin.jpg";
import series from "./playImage/series.jpg";
import { NavLink, useParams, useLocation } from "react-router-dom";
import { useState } from "react";
import Skeleton from "../../components/Skeleton";
import { Helmet } from "react-helmet-async";
import logo from "../../components/images/logo.png";

const Playstatistics = ({ dark, playerData, loading, error }) => {
  const { slug } = useParams();
  const location = useLocation();

  const fallbackPlayer = location?.state?.player || {};

  const player = playerData || {};

  const passedLeagueSlug = location?.state?.leagueSlug;

  const fallbackSlug =
    playerData?.statistics?.[0]?.league?.name
      ?.trim()
      ?.toLowerCase()
      ?.replace(/\s+/g, "") || "";

  const leagueSlug = passedLeagueSlug || fallbackSlug;

  const Name = playerData?.statistics?.[0]?.league?.name
    ?.trim()
    ?.replace(/\s+/g, "-");

  const [selectedCompetition, setSelectedCompetition] = useState("all");

  const competitions = (playerData?.statistics || [])
    .map((stat) => ({
      id: stat?.league?.id,
      name: stat?.league?.name,
      season: stat?.league?.season,
    }))
    .filter((comp) => comp.name)
    .sort((a, b) => a.name.localeCompare(b.name));

  const stats =
    selectedCompetition === "all"
      ? (playerData?.statistics || []).reduce(
          (acc, stat) => ({
            games: {
              appearences:
                (acc.games?.appearences || 0) + (stat.games?.appearences || 0),

              minutes: (acc.games?.minutes || 0) + (stat.games?.minutes || 0),
            },

            goals: {
              total: (acc.goals?.total || 0) + (stat.goals?.total || 0),

              assists: (acc.goals?.assists || 0) + (stat.goals?.assists || 0),
            },

            cards: {
              yellow: (acc.cards?.yellow || 0) + (stat.cards?.yellow || 0),

              red: (acc.cards?.red || 0) + (stat.cards?.red || 0),
            },

            penalty: {
              scored: (acc.penalty?.scored || 0) + (stat.penalty?.scored || 0),

              missed: (acc.penalty?.missed || 0) + (stat.penalty?.missed || 0),
            },

            shots: {
              on: (acc.shots?.on || 0) + (stat.shots?.on || 0),
            },

            tackles: {
              total: (acc.tackles?.total || 0) + (stat.tackles?.total || 0),
            },

            fouls: {
              committed:
                (acc.fouls?.committed || 0) + (stat.fouls?.committed || 0),

              drawn: (acc.fouls?.drawn || 0) + (stat.fouls?.drawn || 0),
            },

            offsides: (acc.offsides || 0) + (stat.offsides || 0),

            team: stat.team || acc.team,
          }),
          {
            games: {
              appearences: 0,
              minutes: 0,
            },
            goals: {
              total: 0,
              assists: 0,
            },
            cards: {
              yellow: 0,
              red: 0,
            },
            penalty: {
              scored: 0,
              missed: 0,
            },
            shots: {
              on: 0,
            },
            tackles: {
              total: 0,
            },
            fouls: {
              committed: 0,
              drawn: 0,
            },
            offsides: 0,
          },
        )
      : playerData?.statistics?.find(
          (s) => String(s.league?.id) === String(selectedCompetition),
        ) || {};

  const team = stats?.team || {};

  const mystats = [
    {
      item: "Goal per minute",
      score:
        stats?.games?.minutes && stats?.goals?.total
          ? Math.floor(stats.games.minutes / stats.goals.total)
          : 0,
    },
    { item: "Yellow card", score: stats?.cards?.yellow ?? 0 },
    { item: "Red card", score: stats?.cards?.red ?? 0 },
    { item: "Penalty goals", score: stats?.penalty?.scored ?? 0 },
    { item: "Penalty missed", score: stats?.penalty?.missed ?? 0 },
    { item: "Shots on target", score: stats?.shots?.on ?? 0 },
    { item: "Offside", score: stats?.offsides ?? 0 },
    { item: "Corners", score: 0 },
    { item: "Crosses", score: 0 },
    { item: "Tackles", score: stats?.tackles?.total ?? 0 },
    { item: "Foul committed", score: stats?.fouls?.committed ?? 0 },
    { item: "Foul suffered", score: stats?.fouls?.drawn ?? 0 },
    { item: "Freekick goals", score: 0 },
    { item: "Goal outside box", score: 0 },
    { item: "Hit woodwork", score: 0 },
  ];

  return (
    <>
      <Helmet>
        <title>
          {`${fallbackPlayer?.name || player?.name || "Player"} Stats, Performance & Season Statistics | FadaSports`}
        </title>

        <meta
          name="description"
          content={`View ${
            fallbackPlayer?.name || player?.name || "this player"
          }'s detailed football statistics including goals, assists, appearances, minutes played, ratings, and season performance analysis for ${
            team?.name || "their club"
          } in ${stats?.league?.name || "top competitions"} on FadaSports.`}
        />

        <meta
          name="keywords"
          content={`
      ${fallbackPlayer?.name || player?.name},
      ${team?.name || ""},
      ${stats?.league?.name || ""},
      player statistics,
      football stats,
      goals,
      assists,
      appearances,
      minutes played,
      player ratings,
      season performance,
      football analysis,
      FadaSports
    `}
        />

        <meta name="author" content="FadaSports" />

        <meta name="robots" content="index, follow, max-image-preview:large" />

        <meta name="theme-color" content="#000000" />

        <link
          rel="canonical"
          href={`https://fadasports.com/players/${slug}/statistics`}
        />

        <meta property="og:type" content="profile" />

        <meta property="og:site_name" content="FadaSports" />

        <meta
          property="og:url"
          content={`https://fadasports.com/players/${slug}/statistics`}
        />

        <meta
          property="og:title"
          content={`${fallbackPlayer?.name || player?.name || "Player"} Stats & Performance | FadaSports`}
        />

        <meta
          property="og:description"
          content={`Detailed season statistics for ${
            fallbackPlayer?.name || player?.name || "this player"
          } including goals, assists, appearances and performance ratings.`}
        />

        <meta
          property="og:image"
          content="https://fadasports.com/static/media/mobilelogo.d0257d49e02d3094cce0.png"
        />

        <meta
          property="og:image:alt"
          content={`${fallbackPlayer?.name || player?.name || "Player"} statistics`}
        />

        <meta property="og:locale" content="en_US" />

        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:title"
          content={`${fallbackPlayer?.name || player?.name || "Player"} Stats | FadaSports`}
        />

        <meta
          name="twitter:description"
          content={`View ${
            fallbackPlayer?.name || player?.name || "player"
          }'s season stats including goals, assists, appearances and ratings.`}
        />

        <meta
          name="twitter:image"
          content={fallbackPlayer?.photo || player?.photo || { logo }}
        />

        <meta name="twitter:site" content="@FadaSports" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SportsEvent",

            name: `${fallbackPlayer?.name || player?.name} Season Statistics`,

            athlete: {
              "@type": "Person",
              name: fallbackPlayer?.name || player?.name || "Football Player",
              image: fallbackPlayer?.photo || player?.photo || { logo },
            },

            description: `Season statistics for ${
              fallbackPlayer?.name || player?.name || "this player"
            } including goals, assists, appearances and performance metrics.`,

            url: `https://fadasports.com/players/${slug}/statistics`,

            sport: "Football",

            location: {
              "@type": "SportsTeam",
              name: team?.name || fallbackPlayer?.team?.name || "",
            },
          })}
        </script>
      </Helmet>

      <div className="homer">
        <div className={`playerWrapper`}>
          <section className="firstsection">
            <nav>
              <img
                src={fallbackPlayer?.photo || player?.photo}
                alt={player?.name}
              />
              <div>
                <p style={dark ? { color: "#fafafaff" } : { color: "#161819" }}>
                  {fallbackPlayer?.name || player?.name}
                </p>
              </div>
            </nav>

            <span>
              <strong
                style={dark ? { color: "#c7c7c7ff" } : { color: "#363636ff" }}>
                {player?.age || fallbackPlayer?.age
                  ? `${player?.age || fallbackPlayer?.age} years`
                  : "Loading..."}{" "}
                {player?.nationality || fallbackPlayer?.nationality || ""}
              </strong>

              <strong
                style={dark ? { color: "#c5c3c3ff" } : { color: "#3a3a3aff" }}>
                {team?.name || fallbackPlayer?.team?.name || ""}
              </strong>
            </span>

            <menu>
              <ul style={dark ? { backgroundColor: "#333333ff" } : {}}>
                <li>
                  <NavLink
                    to={`/players/${slug}`}
                    state={{ player: fallbackPlayer, leagueSlug }}
                    className="linkname"
                    style={
                      dark
                        ? {
                            color: "#fafafaff",
                            backgroundColor: "transparent",
                          }
                        : {
                            color: "#3F3E3E",
                            backgroundColor: "transparent",
                          }
                    }>
                    Overview
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to={`/players/${slug}/news`}
                    state={{ player: fallbackPlayer, leagueSlug }}
                    className="linkname"
                    style={
                      dark ? { color: "#fafafaff" } : { color: "#3F3E3E" }
                    }>
                    News
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to={`/players/${slug}/statistics`}
                    state={{ player: fallbackPlayer, leagueSlug }}
                    className="myactive"
                    style={
                      dark ? { color: "#fafafaff" } : { color: "#3F3E3E" }
                    }>
                    Statistics
                  </NavLink>
                </li>
              </ul>
            </menu>
          </section>

          {loading ? (
            <Skeleton dark={dark} count={6} />
          ) : error ? (
            <div
              style={{
                padding: "40px 20px",
                color: "red",
              }}>
              {error}
            </div>
          ) : !playerData ? (
            <div
              style={{
                padding: "40px 20px",
                color: dark ? "#fff" : "#161819",
              }}>
              No player data found.
            </div>
          ) : (
            <section className="seventhsection">
              <div>
                <select
                  name="competition"
                  value={selectedCompetition}
                  onChange={(e) => setSelectedCompetition(e.target.value)}
                  style={
                    dark
                      ? {
                          backgroundColor: "#1f1f1fff",
                          border: "1px solid #202020ff",
                          color: "white",
                        }
                      : {}
                  }>
                  <option value="all">All competition</option>

                  {competitions.map((comp) => (
                    <option key={comp.id} value={comp.id}>
                      {comp.name} ({comp.season})
                    </option>
                  ))}
                </select>
              </div>

              <div className="cards">
                <nav style={{ backgroundColor: "#B1A230" }}>
                  <h2>{stats?.games?.appearences || 0}</h2>
                  <strong>Appearance</strong>
                </nav>

                <nav style={{ backgroundColor: "#8B1F0C" }}>
                  <h2>{stats?.goals?.total || 0}</h2>
                  <strong>Goals</strong>
                </nav>

                <nav style={{ backgroundColor: "#304EB1" }}>
                  <h2>{stats?.goals?.assists || 0}</h2>
                  <strong>Assist</strong>
                </nav>

                <nav style={{ backgroundColor: "#B1304C" }}>
                  <h2>{stats?.games?.minutes || 0}</h2>
                  <strong>minutes played</strong>
                </nav>
              </div>

              <h1 style={dark ? { color: "#fafafaff" } : { color: "#161819" }}>
                More season stats
              </h1>

              <div>
                <table style={dark ? { backgroundColor: "#181818ff" } : {}}>
                  <tbody>
                    <tr>
                      <th></th>
                      <th
                        className="total"
                        style={
                          dark ? { color: "#fafafaff" } : { color: "#3F3E3E" }
                        }>
                        Total
                      </th>
                    </tr>

                    {mystats.map((mystat, index) => (
                      <tr key={index}>
                        <th
                          style={
                            dark ? { color: "#fafafaff" } : { color: "#3F3E3E" }
                          }>
                          {mystat.item}
                        </th>
                        <th
                          style={
                            dark ? { color: "#fafafaff" } : { color: "#3F3E3E" }
                          }>
                          {mystat.score}
                        </th>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
};

export default Playstatistics;
